import styles from "./dialog.module.css"

import React, { useEffect, useRef, useState } from "react";
import { isArrayEmpty, isNullOrEmpty, isObjectNull, stopPropagation } from "../../helpers/ObjectHelpers";
import { Dialog as KendoDialog, DialogActionsBar as KendoDialogActionsBar } from "@progress/kendo-react-dialogs";
import { plusIcon, xIcon } from "@progress/kendo-svg-icons";
import { TextBox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import SourceApiRepository from "../../../repositories/api/SourceApiRepository";
import { ListView } from "@progress/kendo-react-listview";
import { getElementById } from "../../helpers/ElementHelpers";
import { Button } from "@progress/kendo-react-buttons";
import { isWhitespace as isOnlyWhitespace } from "components/helpers/TextHelpers";
import Busy from "../overlay/components/Busy";

export const PersonSearchDialog = ({
    onClose,
    onSelect,
    onAdd,
    personTypes = [],
    showPersonType = true,
    canChangePersonType = true,
    excludeIds = [],
    allTypes = false
}) => {
    const [persons, setPersons] = useState([]);
    const [isBusy, setIsBusy] = useState(false);

    const PersonSearchResultRender = (props) => {
        const item = props.dataItem;

        return (
            <div
                className={`k-listview-item dialog-row p-2 border-bottom ${styles.resultGridPerson}`}
                style={{
                    margin: 0,
                }}
                onClick={() => {
                    onSelect(item);
                }}
            >
                <div>
                    {item.firstName} {item.lastName}
                </div>
                <div className={`${styles.textTruncate}`}>{item.personType}</div>
                <div className={`${styles.textTruncate}`}>{item.companyName}</div>
            </div>
        );
    };

    return (
        <KendoDialog
            title={
                <PersonSearchTitle
                    personTypes={personTypes}
                    canChangePersonType={canChangePersonType}
                    showPersonType={showPersonType}
                    onSearch={onSearchHandler}
                    onAdd={onAdd}
                    excludeIds={excludeIds}
                    isBusy={isBusy}
                    setIsBusy={setIsBusy}
                    allTypes={allTypes}
                />
            }
            onClose={onClose}
            className="dialog-list">
            {isBusy ? (
                <div className={styles.searchLoader}>
                    <Busy isBusy={isBusy} />
                </div>
            ) : (
                <ListView data={persons} item={PersonSearchResultRender} />
            )}

            <KendoDialogActionsBar>
                <Button onClick={onClose} svgIcon={xIcon}>
                    Avbryt
                </Button>
            </KendoDialogActionsBar>
        </KendoDialog>
    );

    function onSearchHandler(result) {
        setPersons(result);
    }
};

const PersonSearchTitle = ({
    personTypes = [],
    canChangePersonType,
    showPersonType,
    onSearch,
    onAdd,
    excludeIds = [],
    isBusy,
    setIsBusy,
    allTypes = false
}) => {
    const textBoxId = "DialogSearchTextBox";

    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedPersonType, setSelectedPersonType] = useState(null);

    const componentRef = useRef({
        timer: null,
    });
    const { current: localRef } = componentRef;

    useEffect(() => {
        initializeAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isBusy) return;
        const elem = getElementById(textBoxId);
        elem.focus();
    }, [isBusy]);

    useEffect(() => {
        clearTimeout(localRef.timer);
        localRef.timer = setTimeout(() => {
            onSearchAsync();
        }, 1250);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    useEffect(() => {
        onSearchAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPersonType]);

    return (
        <>
            <div className="k-d-grid grid-cols-fr-auto k-gap-2 k-w-full">
                <div>
                    <TextBox
                        id={textBoxId}
                        disabled={isBusy}
                        placeholder="Søk med Fornavn / Etternavn / FB nr. / Brukernavn"
                        value={searchText}
                        onChange={onSearchTextChange}
                    />
                </div>
                {showPersonType && (
                    <div style={{ width: "200px" }}>
                        <DropDownList
                            disabled={isBusy || !canChangePersonType}
                            data={data}
                            textField="personTypeNor"
                            defaultItem={{ personTypeNor: "Alle persontyper" }}
                            value={selectedPersonType}
                            onChange={onPersonTypeChange}
                        />
                    </div>
                )}
                {isPersonTypeNavigatorOrCaptain(personTypes) && (
                    <Button
                        svgIcon={plusIcon}
                        disabled={isBusy || isNullOrEmpty(searchText) || isOnlyWhitespace(searchText)}
                        className="k-mr-2"
                        onClick={() => onAdd({ fullName: searchText })}
                    ></Button>
                )}
            </div>
        </>
    );

    async function initializeAsync() {
        const response =
            allTypes ?
                await SourceApiRepository.getAllPersonTypesAsync()
                : await SourceApiRepository.getPersonTypesAsync();
        if (response.ok) {
            let data = await response.json();

            if (!isArrayEmpty(personTypes)) {
                data = data.filter((d) => personTypes.includes(d.systemName));
                const personType = data.find((d) => d.systemName === personTypes[0]);
                if (!isObjectNull(personType)) {
                    setSelectedPersonType(personType);
                }
            }

            setData(data);
        }
    }

    function onPersonTypeChange(e) {
        setSelectedPersonType(e.target.value);
    }

    function onSearchTextChange(e) {
        stopPropagation(e);
        setSearchText(e.target.value);
    }

    function isPersonTypeNavigatorOrCaptain(personTypes) {
        return personTypes.includes("NAVIGATOR") || personTypes.includes("CAPTAIN");
    }

    async function onSearchAsync() {
        clearTimeout(localRef.timer);
        if (isBusy) return;

        if (searchText.length < 3) {
            onSearch([]);
        } else {
            setIsBusy(true);
            let personTypes = [];
            if(showPersonType){
                if (selectedPersonType && selectedPersonType.hasOwnProperty("systemName")) {
                    personTypes.push(selectedPersonType.systemName);
                }
            }

            if(personTypes.length === 0) {
                personTypes = data.map(pt => pt.systemName);
            }

            const query = {
                searchText: searchText,
                types: personTypes,
                excludeIds: isObjectNull(excludeIds) ? [] : excludeIds,
            };

            const response = await SourceApiRepository.searchPersonsAsync(query);
            if (response.ok) {
                const data = await response.json();
                onSearch(data);
            } else {
                onSearch([]);
            }
            setTimeout(() => {
                setIsBusy(false);
            }, 1000);
        }
    }
};
